import { useState, useRef, useEffect } from 'react'
import {
  Plus, Search, BookOpen, FolderPlus,
  PanelLeftClose, PanelLeftOpen, CalendarDays,
  ChevronRight, Settings, Palette,
  X, Copy, Mail, Check, ShieldCheck,
  User, Folder, MoreHorizontal
} from 'lucide-react'
import { VenterIcon } from '@/assets/icons/VenterIcon'
import { FavorittIcon } from '@/assets/icons/FavorittIcon'
import { GulSirkelIcon } from '@/assets/icons/GulSirkelIcon'
import { BlaSirkelIcon } from '@/assets/icons/BlaSirkelIcon'
import { ConvContextMenu } from './ConvContextMenu'
import { CreateProjectModal, type Project } from './CreateProjectModal'
import { MoveToProjectModal } from './MoveToProjectModal'
import { buildShareLink, moveConversationToProject, markConversationPublic } from '@/services/conversationService'
import { fetchProjects, createProjectInDb } from '@/services/projectService'
import { useApp } from '@/store/AppContext'
import { SidebarConversationSkeleton } from '@/components/ui/SkeletonLoader'
import { getTranslations } from '@/i18n'
import { useAuth } from '@/hooks/useAuth'
import { useCredits } from '@/hooks/useCredits'
import type { Conversation } from '@/types'

// Logo variants: Sine-hvit.svg = white logo for dark mode, Sine-sort.svg = dark logo for light mode
const LOGO_FOR_DARK_BG = "/Sine-hvit.svg"
const LOGO_FOR_LIGHT_BG = "/Sine-sort.svg"

interface SidebarProps {
  onNavigate?: (page: string) => void
  currentPage?: string
  activeAgentRunId?: string | null
  onSelectConversation?: (id: string) => void
  onNewChat?: () => void
}

// ─── Slett-bekreftelsesdialog ─────────────────────────────────
function DeleteConfirmDialog({
  title,
  onConfirm,
  onCancel,
}: {
  title: string
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: '#1e1e20',
          border: '1px solid #2e2e30',
          borderRadius: 12,
          padding: '24px',
          width: 360,
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          position: 'relative',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Lukk-knapp */}
        <button
          onClick={onCancel}
          style={{
            position: 'absolute', top: 14, right: 14,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: '#6A6A6A', display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 24, height: 24, borderRadius: 6,
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#2a2a2c')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <X size={15} />
        </button>

        <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 600, color: '#E5E5E5' }}>
          Slett denne samtalen?
        </h3>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#8A8A8A', lineHeight: 1.5 }}>
          <strong style={{ color: '#B0B0B0' }}>"{title.length > 40 ? title.slice(0, 40) + '…' : title}"</strong>
          {' '}vil bli slettet permanent og kan ikke gjenopprettes.
        </p>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 18px', borderRadius: 8, border: '1px solid #3A3A3A',
              background: 'transparent', color: '#C0C0C0', fontSize: 13, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = '#2a2a2c')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            Avbryt
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '8px 18px', borderRadius: 8, border: 'none',
              background: '#ef4444', color: '#fff', fontSize: 13, cursor: 'pointer',
              fontFamily: 'inherit', fontWeight: 500,
            }}
            onMouseEnter={e => (e.currentTarget.style.background = '#dc2626')}
            onMouseLeave={e => (e.currentTarget.style.background = '#ef4444')}
          >
            Slett
          </button>
        </div>
      </div>
    </div>
  )
}

export function Sidebar({ onNavigate, currentPage = 'chat', activeAgentRunId, onSelectConversation, onNewChat: onNewChatProp }: SidebarProps) {
  const {
    conversations,
    conversationsLoaded,
    activeConversationId,
    setActiveConversationId,
    deleteConversation,
    toggleFavorite,
    renameConversation,
    settings,
    sidebarOpen,
    setSidebarOpen,
    setUserClosedSidebar,
    setSettingsOpen,
    setActiveProjectInstructions,
  } = useApp()
  const { user } = useAuth()
  const { profile } = useCredits(user?.id ?? null)
  const isAdminUser = Boolean(user?.isAdmin || profile?.isAdmin || profile?.role === 'admin')
  const [activeFilter, setActiveFilter] = useState<'all' | 'agents' | 'manual' | 'schedule'>('all')

  const t = getTranslations(settings.language)
  const effectiveTheme = settings.theme === 'system'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : settings.theme
  const logoSrc = effectiveTheme === 'light' ? LOGO_FOR_LIGHT_BG : LOGO_FOR_DARK_BG
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [referralOpen, setReferralOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null)
  const [createProjectOpen, setCreateProjectOpen] = useState(false)
  const [projects, setProjects] = useState<Project[]>(() => {
    try { return JSON.parse(localStorage.getItem('sine_projects') || '[]') } catch { return [] }
  })
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set())
  const [moveToProjectConvId, setMoveToProjectConvId] = useState<string | null>(null)
  const [contextMenu, setContextMenu] = useState<{
    convId: string; convTitle: string; isFavorite: boolean; x: number; y: number
  } | null>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)

  // Load projects from Supabase on mount / user change
  useEffect(() => {
    if (!user?.id) return
    fetchProjects(user.id).then(dbProjects => {
      if (dbProjects.length > 0) {
        setProjects(dbProjects)
        try { localStorage.setItem('sine_projects', JSON.stringify(dbProjects)) } catch { /* ignore */ }
      }
    }).catch(() => { /* fallback to localStorage */ })
  }, [user?.id])

  const handleCreateProject = (data: Omit<Project, 'id' | 'createdAt'>) => {
    const newProject: Project = {
      ...data,
      id: Math.random().toString(36).slice(2),
      createdAt: new Date(),
    }
    const updated = [...projects, newProject]
    setProjects(updated)
    try { localStorage.setItem('sine_projects', JSON.stringify(updated)) } catch { /* ignore */ }
    // Persist to Supabase
    if (user?.id) {
      createProjectInDb(user.id, newProject).catch(console.error)
    }
  }

  const handleMoveToProject = async (projectId: string | null) => {
    if (!moveToProjectConvId) return
    // Optimistically update local state
    setProjects(prev => prev) // trigger re-render
    // Update conversation's projectId in Supabase
    await moveConversationToProject(moveToProjectConvId, projectId).catch(console.error)
    // Force reload of conversations to reflect change
    window.dispatchEvent(new CustomEvent('sine-conv-project-updated', {
      detail: { convId: moveToProjectConvId, projectId }
    }))
    setMoveToProjectConvId(null)
  }

  const handleNewChat = () => {
    if (onNewChatProp) {
      onNewChatProp()
    } else {
      // Just clear active conversation; actual creation happens on first message send
      setActiveConversationId(null)
      onNavigate?.('chat')
    }
  }

  const handleDeleteRequest = (id: string, title: string) => {
    setDeleteTarget({ id, title })
  }

  const handleDeleteConfirm = () => {
    if (deleteTarget) {
      deleteConversation(deleteTarget.id)
      setDeleteTarget(null)
    }
  }

  const handleOpenContextMenu = (
    e: React.MouseEvent, convId: string, convTitle: string, isFavorite: boolean,
  ) => {
    e.stopPropagation()
    e.preventDefault()
    setContextMenu({ convId, convTitle, isFavorite, x: e.clientX, y: e.clientY })
  }

  const handleRenameCommit = () => {
    if (renamingId && renameDraft.trim()) {
      renameConversation(renamingId, renameDraft.trim())
    }
    setRenamingId(null)
    setRenameDraft('')
  }

  const handleShare = async (convId: string) => {
    await markConversationPublic(convId)
    const link = buildShareLink(convId)
    navigator.clipboard.writeText(link).catch(() => {})
    // Brief visual feedback via window toast if available — fallback silent
  }

  const groupedConversations = () => {
    const favorites: typeof conversations = []
    const today: typeof conversations = []
    const yesterday: typeof conversations = []
    const week: typeof conversations = []
    const older: typeof conversations = []
    const now = new Date()
    for (const c of conversations) {
      if (c.isFavorite) { favorites.push(c); continue }
      const diff = now.getTime() - c.updatedAt.getTime()
      const days = diff / (1000 * 60 * 60 * 24)
      if (days < 1) today.push(c)
      else if (days < 2) yesterday.push(c)
      else if (days < 7) week.push(c)
      else older.push(c)
    }
    return { favorites, today, yesterday, week, older }
  }

  const groups = groupedConversations()

  if (!sidebarOpen) {
    return (
      <div className="sidebar-collapsed">
        {/* Top: logo */}
        <button
          className="sidebar-logo-btn"
          onClick={handleNewChat}
          title="Ny chat"
          style={{ padding: 0, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 2 }}
        >
          <img src={logoSrc} alt="Sine" style={{ width: 22, height: 22, objectFit: 'contain' }} />
        </button>
        {/* Expand button - hidden by default, shown on hover */}
        <button
          className="icon-btn sidebar-expand-btn"
          onClick={() => setSidebarOpen(true)}
          title="Åpne meny"
          style={{ marginBottom: 4 }}
        >
          <PanelLeftOpen size={18} />
        </button>

        <div className="sidebar-divider" style={{ width: 32, height: 1, margin: '4px 0' }} />

        {/* Nav icons */}
        <button className="icon-btn" onClick={handleNewChat} title={t.app.newChat}>
          <Plus size={18} />
        </button>

        <button className="icon-btn" onClick={() => onNavigate?.('search')} title={t.app.search}>
          <Search size={18} />
        </button>
        <button className="icon-btn" onClick={() => onNavigate?.('library')} title={t.app.library}>
          <BookOpen size={18} />
        </button>
        <button className="icon-btn" onClick={() => onNavigate?.('calendar')} title={t.app.calendar}>
          <CalendarDays size={18} />
        </button>

        {/* Spacer pushes bottom icons down */}
        <div style={{ flex: 1 }} />

        <div className="sidebar-divider" style={{ width: 32, height: 1, margin: '4px 0' }} />

        {/* Bottom icons */}
        {isAdminUser && (
          <button className="icon-btn" onClick={() => onNavigate?.('admin')} title="Admin">
            <ShieldCheck size={18} />
          </button>
        )}
        <button className="icon-btn" onClick={() => setSettingsOpen(true)} title={t.app.settings}>
          <User size={18} />
        </button>
      </div>
    )
  }

  return (
    <>
      <div className="sidebar">
        {/* Mobile topbar — only visible on mobile via CSS */}
        <div className="mobile-topbar">
          <button className="mobile-avatar-btn" onClick={() => setSettingsOpen(true)} title="Profil">
            {user?.email ? user.email[0].toUpperCase() : <User size={16} />}
          </button>
          <span className="mobile-topbar-center mobile-topbar-logo">sine</span>
          <div className="mobile-topbar-right">
            <button className="mobile-topbar-btn" onClick={() => onNavigate?.('search')} title="Søk">
              <Search size={20} />
            </button>
          </div>
        </div>
        {/* Mobile filter pills — only visible on mobile via CSS */}
        <div className="mobile-filter-pills">
          {(['all', 'agents', 'manual', 'schedule'] as const).map(f => (
            <button
              key={f}
              className={`mobile-filter-pill${activeFilter === f ? ' active' : ''}`}
              onClick={() => setActiveFilter(f)}
            >
              {f === 'all' ? 'All' : f === 'agents' ? 'Agents' : f === 'manual' ? 'Manual' : 'Schedule'}
            </button>
          ))}
        </div>

        {/* Desktop: Logo + collapse */}
        <div className="sidebar-logo sidebar-logo-desktop">
          <button className="sidebar-logo-btn" onClick={handleNewChat} title="Ny chat">
            <img
              src={logoSrc}
              alt="Sine"
              style={{ height: 22, width: 'auto' }}
            />
          </button>
          <button className="icon-btn" onClick={() => { setSidebarOpen(false); setUserClosedSidebar(true); }} title={t.app.closeSidebar}>
            <PanelLeftClose size={18} />
          </button>
        </div>

        {/* Main nav */}
        <div className="sidebar-nav" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <button className="nav-item highlight" onClick={handleNewChat}>
            <Plus size={18} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.app.newChat}</span>
          </button>

          <button
            className={`nav-item${currentPage === 'search' ? ' active' : ''}`}
            onClick={() => onNavigate?.('search')}
          >
            <Search size={18} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.app.search}</span>
            <span style={{ fontSize: 11, color: '#3A3A3A', flexShrink: 0 }}>⌘K</span>
          </button>
          <button
            className={`nav-item${currentPage === 'library' ? ' active' : ''}`}
            onClick={() => onNavigate?.('library')}
          >
            <BookOpen size={18} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.app.library}</span>
          </button>
          <button
            className={`nav-item${currentPage === 'calendar' ? ' active' : ''}`}
            onClick={() => onNavigate?.('calendar')}
          >
            <CalendarDays size={18} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.app.calendar}</span>
          </button>

          {isAdminUser && (
            <button
              className={`nav-item${currentPage === 'admin' ? ' active' : ''}`}
              onClick={() => onNavigate?.('admin')}
              style={{ color: currentPage === 'admin' ? '#00d4ff' : '#888' }}
            >
              <ShieldCheck size={18} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Admin</span>
            </button>
          )}
        </div>

        <div className="sidebar-divider" />

        {/* Projects */}
        <div className="sidebar-section">
          <div className="sidebar-section-header">
            <span className="sidebar-section-label">{t.app.projects}</span>
            <button className="icon-btn" style={{ width: 20, height: 20 }} onClick={() => setCreateProjectOpen(true)} title={t.app.newProject}>
              <Plus size={12} />
            </button>
          </div>
          {projects.length === 0 ? (
            <button className="nav-item" onClick={() => setCreateProjectOpen(true)}>
              <FolderPlus size={18} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.app.newProject}</span>
            </button>
          ) : (
            <>
              {projects.map(project => (
                <div key={project.id}>
                  <button
                    className="nav-item"
                    onClick={() => {
                      setExpandedProjects(prev => {
                        const next = new Set(prev)
                        if (next.has(project.id)) next.delete(project.id)
                        else next.add(project.id)
                        return next
                      })
                      // Set active project instructions for chat
                      setActiveProjectInstructions(project.instructions || null)
                    }}
                    style={{ paddingLeft: 8 }}
                  >
                    <Folder size={15} style={{ flexShrink: 0, color: '#7A7A7A' }} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>{project.name}</span>
                    <ChevronRight
                      size={12}
                      style={{
                        flexShrink: 0,
                        color: '#5A5A5A',
                        transform: expandedProjects.has(project.id) ? 'rotate(90deg)' : 'none',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </button>
                  {expandedProjects.has(project.id) && (
                    <div style={{ paddingLeft: 24 }}>
                      {conversations
                        .filter((c: Conversation) => c.projectId === project.id)
                        .map(c => (
                          <button
                            key={c.id}
                            className={`nav-item${activeConversationId === c.id ? ' active' : ''}`}
                            onClick={() => { setActiveConversationId(c.id); onNavigate?.('chat') }}
                            onContextMenu={e => handleOpenContextMenu(e, c.id, c.title, c.isFavorite ?? false)}
                            style={{ fontSize: 12, paddingLeft: 8 }}
                          >
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>
                          </button>
                        ))
                      }
                      {conversations.filter((c: Conversation) => c.projectId === project.id).length === 0 && (
                        <p style={{ fontSize: 11, color: '#5A5A5A', padding: '4px 8px' }}>Ingen samtaler</p>
                      )}
                      {/* New chat in project */}
                      <button
                        className="nav-item"
                        onClick={() => {
                          // Start new chat with project context
                          setActiveConversationId(null)
                          setActiveProjectInstructions(project.instructions ?? null)
                          onNavigate?.('chat')
                        }}
                        style={{ fontSize: 11, paddingLeft: 8, opacity: 0.55, gap: 6 }}
                      >
                        <Plus size={12} style={{ flexShrink: 0 }} />
                        <span>Ny chat i prosjekt</span>
                      </button>
                    </div>
                  )}
                </div>
              ))}
              <button className="nav-item" onClick={() => setCreateProjectOpen(true)} style={{ opacity: 0.6 }}>
                <Plus size={14} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>{t.app.newProject}</span>
              </button>
            </>
          )}
        </div>

        {/* Conversations list */}
        <div className="sidebar-conversations">
          {!conversationsLoaded ? (
            <SidebarConversationSkeleton />
          ) : conversations.length === 0 ? (
            <p style={{ fontSize: 12, padding: '12px 8px', textAlign: 'center', color: '#3A3A3A', lineHeight: 1.5 }}>
              Ingen samtaler ennå.{' '}
              <span style={{ color: '#1A93FE', cursor: 'pointer' }} onClick={handleNewChat}>
                Start en ny
              </span>
            </p>
          ) : (
            <>
              {/* Favourites first */}
              <ConvGroup label={settings.language === 'no' ? 'Favoritter' : 'Favourites'} conversations={groups.favorites} activeId={activeConversationId} hoveredId={hoveredId} onHover={setHoveredId} onSelect={(id) => { if (onSelectConversation) { onSelectConversation(id) } else { setActiveConversationId(id); onNavigate?.('chat') } }} onContextMenu={handleOpenContextMenu} activeAgentRunId={activeAgentRunId} renamingId={renamingId} renameDraft={renameDraft} onRenameDraftChange={setRenameDraft} onRenameCommit={handleRenameCommit} renameInputRef={renameInputRef} isDark={effectiveTheme === 'dark'} />
              <ConvGroup label={settings.language === 'no' ? 'I dag' : 'Today'} conversations={groups.today} activeId={activeConversationId} hoveredId={hoveredId} onHover={setHoveredId} onSelect={(id) => { if (onSelectConversation) { onSelectConversation(id) } else { setActiveConversationId(id); onNavigate?.('chat') } }} onContextMenu={handleOpenContextMenu} activeAgentRunId={activeAgentRunId} renamingId={renamingId} renameDraft={renameDraft} onRenameDraftChange={setRenameDraft} onRenameCommit={handleRenameCommit} renameInputRef={renameInputRef} isDark={effectiveTheme === 'dark'} />
              <ConvGroup label={settings.language === 'no' ? 'I går' : 'Yesterday'} conversations={groups.yesterday} activeId={activeConversationId} hoveredId={hoveredId} onHover={setHoveredId} onSelect={(id) => { if (onSelectConversation) { onSelectConversation(id) } else { setActiveConversationId(id); onNavigate?.('chat') } }} onContextMenu={handleOpenContextMenu} activeAgentRunId={activeAgentRunId} renamingId={renamingId} renameDraft={renameDraft} onRenameDraftChange={setRenameDraft} onRenameCommit={handleRenameCommit} renameInputRef={renameInputRef} isDark={effectiveTheme === 'dark'} />
              <ConvGroup label={settings.language === 'no' ? 'Siste 7 dager' : 'Last 7 days'} conversations={groups.week} activeId={activeConversationId} hoveredId={hoveredId} onHover={setHoveredId} onSelect={(id) => { if (onSelectConversation) { onSelectConversation(id) } else { setActiveConversationId(id); onNavigate?.('chat') } }} onContextMenu={handleOpenContextMenu} activeAgentRunId={activeAgentRunId} renamingId={renamingId} renameDraft={renameDraft} onRenameDraftChange={setRenameDraft} onRenameCommit={handleRenameCommit} renameInputRef={renameInputRef} isDark={effectiveTheme === 'dark'} />
              <ConvGroup label={settings.language === 'no' ? 'Eldre' : 'Older'} conversations={groups.older} activeId={activeConversationId} hoveredId={hoveredId} onHover={setHoveredId} onSelect={(id) => { if (onSelectConversation) { onSelectConversation(id) } else { setActiveConversationId(id); onNavigate?.('chat') } }} onContextMenu={handleOpenContextMenu} activeAgentRunId={activeAgentRunId} renamingId={renamingId} renameDraft={renameDraft} onRenameDraftChange={setRenameDraft} onRenameCommit={handleRenameCommit} renameInputRef={renameInputRef} isDark={effectiveTheme === 'dark'} />
            </>
          )}
        </div>

        {/* Bottom */}
        <div className="sidebar-bottom">
          {/* Referral-kort */}
          <button className="referral-banner" onClick={() => setReferralOpen(true)}>
            <div className="referral-icon-wrap">
              <img className="referral-hand-icon referral-hand-icon--dark" src="/hand-icon-light.svg" alt="" aria-hidden="true" />
              <img className="referral-hand-icon referral-hand-icon--light" src="/hand-icon-dark.svg" alt="Inviter" />
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div className="referral-title">Del Sine med en venn</div>
              <div className="referral-sub">Få 100 kreditter hver</div>
            </div>
            <ChevronRight size={13} style={{ color: '#5A5A5A', flexShrink: 0 }} />
          </button>

           <div className="sidebar-icon-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <button className="icon-btn" onClick={() => setSettingsOpen(true)} title="Innstillinger">
                <Settings size={18} />
              </button>
              <button className="icon-btn" onClick={() => { setSettingsOpen(true) }} title="Personalisering">
                <Palette size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile FAB — new chat button, only visible on mobile via CSS */}
      <button
        className="mobile-fab"
        onClick={handleNewChat}
        title="Ny samtale"
        /* visibility controlled by mobile.css */
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="12" y1="9" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <line x1="9" y1="12" x2="15" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>

      {/* Referral Modal */}
      {referralOpen && <ReferralModal onClose={() => setReferralOpen(false)} />}

      {/* Create Project Modal */}
      {createProjectOpen && (
        <CreateProjectModal
          onClose={() => setCreateProjectOpen(false)}
          onCreate={handleCreateProject}
          connectorStatuses={settings.connectorStatuses ?? {}}
        />
      )}

      {/* Slett-bekreftelsesdialog */}
      {deleteTarget && (
        <DeleteConfirmDialog
          title={deleteTarget.title}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* Move to project modal */}
      {moveToProjectConvId && (() => {
        const conv = conversations.find(c => c.id === moveToProjectConvId)
        return (
          <MoveToProjectModal
            convTitle={conv?.title ?? 'Samtale'}
            projects={projects}
            currentProjectId={conv?.projectId}
            onClose={() => setMoveToProjectConvId(null)}
            onMove={handleMoveToProject}
          />
        )
      })()}
      {/* Context menu */}
      {contextMenu && (
        <ConvContextMenu
          convId={contextMenu.convId}
          convTitle={contextMenu.convTitle}
          isFavorite={contextMenu.isFavorite}
          position={{ x: contextMenu.x, y: contextMenu.y }}
          language={settings.language}
          onClose={() => setContextMenu(null)}
          onShare={() => handleShare(contextMenu.convId)}
          onRename={() => {
            const conv = conversations.find(c => c.id === contextMenu.convId)
            setRenameDraft(conv?.title ?? '')
            setRenamingId(contextMenu.convId)
            setTimeout(() => renameInputRef.current?.focus(), 50)
          }}
          onToggleFavorite={() => toggleFavorite(contextMenu.convId)}
          onOpenInNewTab={() => {
            const url = `${window.location.origin}${window.location.pathname}?chat=${contextMenu.convId}`
            window.open(url, '_blank')
          }}
          onMoveToProject={() => {
            setMoveToProjectConvId(contextMenu.convId)
            setContextMenu(null)
          }}
          onDelete={() => handleDeleteRequest(contextMenu.convId, contextMenu.convTitle)}
        />
      )}
    </>
  )
}

function ReferralModal({ onClose }: { onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  const [email, setEmail] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [activeTab, setActiveTab] = useState<'web' | 'mobile'>('web')
  const { user } = useAuth()

  // Build the real invite link from the user's referral_code
  const referralCode = user?.referralCode
  const baseUrl = window.location.origin
  const inviteLink = referralCode
    ? `${baseUrl}/?ref=${referralCode}`
    : `${baseUrl}/`

  const handleCopy = () => {
    navigator.clipboard.writeText(inviteLink).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleSendEmail = () => {
    if (!email || !email.includes('@')) return
    const subject = encodeURIComponent('Prøv Sine AI – din personlige AI-agent')
    const body = encodeURIComponent(
      `Hei!\n\nJeg bruker Sine AI og tenkte du ville like det.\n\nBruk min invitasjonslenke for å registrere deg og få 100 gratis kreditter:\n${inviteLink}\n\nHilsen ${user?.name || 'en venn'}`
    )
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank')
    setEmailSent(true)
    setTimeout(() => setEmailSent(false), 3000)
    setEmail('')
  }

  return (
    <div className="referral-modal-overlay" onClick={onClose}>
      <div className="referral-modal" onClick={e => e.stopPropagation()}>
        {/* Lukk-knapp */}
        <button className="referral-modal-close" onClick={onClose}>
          <X size={16} />
        </button>

        {/* E-post-bilde */}
        <div className="referral-modal-hero">
          <img src="/invite-email.png" alt="Inviter" style={{ width: 100, height: 'auto' }} />
        </div>

        {/* Tittel */}
        <h2 className="referral-modal-title">Inviter for å få kreditter</h2>
        <p className="referral-modal-sub">
          Få <strong>100 kreditter</strong> når en venn registrerer seg med lenken din. Vennen får også <strong>100 kreditter</strong>.
        </p>

        {/* Faner */}
        <div className="referral-modal-tabs">
          <button
            className={`referral-tab${activeTab === 'web' ? ' active' : ''}`}
            onClick={() => setActiveTab('web')}
          >Del på web</button>
          <button
            className={`referral-tab${activeTab === 'mobile' ? ' active' : ''}`}
            onClick={() => setActiveTab('mobile')}
          >Del på mobil</button>
        </div>

        {/* Tab content */}
        {activeTab === 'mobile' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '16px 0' }}>
            <p style={{ fontSize: 13, color: '#888', textAlign: 'center', margin: 0 }}>
              Skann QR-koden for å dele invitasjonslenken din
            </p>
            <div style={{ background: '#fff', borderRadius: 12, padding: 12, display: 'inline-block' }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(inviteLink)}&bgcolor=ffffff&color=000000&margin=4`}
                alt="QR-kode"
                width={180}
                height={180}
                style={{ display: 'block', borderRadius: 6 }}
              />
            </div>
            <p style={{ fontSize: 12, color: '#666', textAlign: 'center', margin: 0, wordBreak: 'break-all', maxWidth: 260 }}>
              {inviteLink}
            </p>
            <button
              className="referral-copy-btn"
              onClick={handleCopy}
              style={{ width: '100%' }}
            >
              {copied ? '✓ Kopiert!' : '📋 Kopier lenke'}
            </button>
          </div>
        )}
        {activeTab === 'web' && (<>
        {/* Kopier lenke */}
        <button className="referral-copy-btn" onClick={handleCopy}>
          {copied ? <Check size={15} /> : <Copy size={15} />}
          {copied ? 'Kopiert!' : 'Kopier invitasjonslenke'}
        </button>

        {/* Facebook */}
        <button
          className="referral-facebook-btn"
          onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(inviteLink)}`, '_blank')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          Inviter Facebook-venner
        </button>

        {/* Sosiale medier */}
        <div className="referral-social-row">
          <button
            className="referral-social-btn"
            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent('Prøv Sine AI: ' + inviteLink)}`, '_blank')}
            title="WhatsApp"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </button>
          <button
            className="referral-social-btn"
            onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent('Prøv Sine AI: ' + inviteLink)}`, '_blank')}
            title="X / Twitter"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </button>
          <button
            className="referral-social-btn"
            onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(inviteLink)}`, '_blank')}
            title="LinkedIn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </button>
          <button
            className="referral-social-btn"
            onClick={() => window.open(`https://www.reddit.com/submit?url=${encodeURIComponent(inviteLink)}`, '_blank')}
            title="Reddit"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
            </svg>
          </button>
        </div>

        {/* Send e-post */}
        <div className="referral-email-section">
          <p className="referral-email-label">Send invitasjons-e-post</p>
          <div className="referral-email-row">
            <input
              type="email"
              className="referral-email-input"
              placeholder="Skriv inn e-postadresse"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSendEmail()}
            />
            <button className="referral-email-send" onClick={handleSendEmail}>
              {emailSent ? <Check size={14} /> : <Mail size={14} />}
              {emailSent ? 'Sendt!' : 'Send'}
            </button>
          </div>
        </div>

        {/* Invitations teller */}
        <div className="referral-stats">
          <div className="referral-stats-header">
            <span>Invitasjoner</span>
            <ChevronRight size={13} />
          </div>
          <div className="referral-stats-row">
            <div className="referral-stat">
              <span className="referral-stat-num">0</span>
              <span className="referral-stat-label">Kreditter</span>
            </div>
            <div className="referral-stat">
              <span className="referral-stat-num">0</span>
              <span className="referral-stat-label">Referanser</span>
            </div>
            <div className="referral-stat-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3A3A3A" strokeWidth="1.5">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
          </div>
         </div>
        </>)}
      </div>
    </div>
  )
}
// ── Helper functions for mobile conv-item ─────────────────────
function formatConvTime(date: Date): string {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const mins = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (mins < 1) return 'Nå'
  if (mins < 60) return `${mins}m`
  if (hours < 24) return `${hours}t`
  if (days < 7) return `${days}d`
  return date.toLocaleDateString('no-NO', { day: 'numeric', month: 'short' })
}

function getConvPreview(conv: { title: string; messages?: { role?: string; content?: string | { type: string; text?: string }[] }[] }): string {
  if (!conv.messages || conv.messages.length === 0) return 'Ingen meldinger ennå'
  // Find last user or assistant message with text content
  for (let i = conv.messages.length - 1; i >= 0; i--) {
    const msg = conv.messages[i]
    if (!msg) continue
    let text = ''
    if (typeof msg.content === 'string') {
      text = msg.content
    } else if (Array.isArray(msg.content)) {
      const textPart = msg.content.find((p: { type: string; text?: string }) => p.type === 'text')
      if (textPart && textPart.text) text = textPart.text
    }
    if (text.trim()) {
      return text.length > 60 ? text.slice(0, 60) + '…' : text
    }
  }
  return conv.title
}

function ConvGroup({
  label, conversations, activeId, hoveredId, onHover, onSelect, onContextMenu,
  activeAgentRunId, renamingId, renameDraft, onRenameDraftChange, onRenameCommit, renameInputRef,
  isDark,
}: {
  label: string
  conversations: {
    id: string; title: string; updatedAt: Date; type?: string;
    isFavorite?: boolean; messages?: { agentStatus?: string; agentRunId?: string }[]; appMode?: string
  }[]
  isDark?: boolean
  activeId: string | null
  hoveredId: string | null
  onHover: (id: string | null) => void
  onSelect: (id: string) => void
  onContextMenu: (e: React.MouseEvent, id: string, title: string, isFavorite: boolean) => void
  activeAgentRunId?: string | null
  renamingId: string | null
  renameDraft: string
  onRenameDraftChange: (v: string) => void
  onRenameCommit: () => void
  renameInputRef: React.RefObject<HTMLInputElement | null>
}) {
  const [menuOpenId] = useState<string | null>(null)
  const liveAgentStatuses = new Set(['connecting', 'planning', 'running', 'reconnecting', 'recovering', 'waiting_approval', 'needs_takeover', 'takeover_active'])

  if (conversations.length === 0) return null
  return (
    <div style={{ marginBottom: 4 }}>
      <p className="conv-group-label">{label}</p>
      {conversations.map(conv => {
        const lastMessage = conv.messages?.[conv.messages.length - 1]
        const lastStatus = lastMessage?.agentStatus
        const lastRunId = lastMessage?.agentRunId
        const isRunning = Boolean(
          activeAgentRunId
          && conv.id === activeId
          && lastRunId === activeAgentRunId
          && lastStatus
          && liveAgentStatuses.has(lastStatus),
        )
        const isFav = conv.isFavorite ?? false
        const needsApproval = isRunning && lastStatus === 'waiting_approval'

        let iconColor: string | undefined
        let iconOpacity = 0.45
        if (isFav) {
          iconColor = '#F59E0B'
          iconOpacity = 1
        } else if (isRunning) {
          // Blue spinning icon for active running agent
          iconOpacity = 1
        } else if (lastStatus === 'completed') {
          iconColor = '#22C55E'
          iconOpacity = 1
        } else if (lastStatus === 'failed' || lastStatus === 'stopped') {
          iconColor = '#EF4444'
          iconOpacity = 1
        } else if (needsApproval) {
          iconOpacity = 1
        }

        const isRenaming = renamingId === conv.id

        return (
          <div
            key={conv.id}
            className={`conv-item${activeId === conv.id ? ' active' : ''}`}
            onClick={() => { if (!isRenaming) onSelect(conv.id) }}
            onMouseEnter={() => onHover(conv.id)}
            onMouseLeave={() => { if (menuOpenId !== conv.id) onHover(null) }}
          >
            {/* Status / favourite icon */}
            <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', width: 20, opacity: iconOpacity }}>
              {needsApproval
                ? <GulSirkelIcon size={20} />
                : isRunning
                  ? <BlaSirkelIcon size={20} />
                  : isFav
                    ? <FavorittIcon size={20} color={iconColor ?? 'currentColor'} />
                    : conv.appMode === 'studio'
                      ? <img
                          src={isDark ? '/studio-icon-white.svg' : '/studio-icon-black.svg'}
                          alt="Studio"
                          style={{ width: 16, height: 16, opacity: 0.7 }}
                        />
                      : <VenterIcon size={20} color={iconColor ?? 'currentColor'} />
              }
            </span>

            {/* Title or inline rename input */}
            {isRenaming ? (
              <input
                ref={renameInputRef}
                value={renameDraft}
                onChange={e => onRenameDraftChange(e.target.value)}
                onBlur={onRenameCommit}
                onKeyDown={e => {
                  e.stopPropagation()
                  if (e.key === 'Enter') onRenameCommit()
                  if (e.key === 'Escape') { onRenameDraftChange(''); onRenameCommit() }
                }}
                onClick={e => e.stopPropagation()}
                style={{
                  flex: 1, background: 'transparent', border: 'none',
                  borderBottom: '1px solid #1A93FE', outline: 'none',
                  color: 'inherit', fontSize: 13, fontFamily: 'inherit',
                  padding: '1px 0', minWidth: 0,
                }}
              />
            ) : (
              <>
                {/* Desktop: single-line title */}
                <span className="conv-item-desktop-title" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {conv.title.length > 26 ? conv.title.slice(0, 26) + '…' : conv.title}
                </span>
                {/* Mobile: title + preview + time */}
                <div className="conv-item-text">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span className="conv-item-title">
                      {conv.title.length > 30 ? conv.title.slice(0, 30) + '…' : conv.title}
                    </span>
                    <span className="conv-item-time">
                      {formatConvTime(conv.updatedAt)}
                    </span>
                  </div>
                  <span className="conv-item-preview">
                    {getConvPreview(conv)}
                  </span>
                </div>
              </>
            )}

            {/* Three-dot menu button on hover */}
            {hoveredId === conv.id && !isRenaming && (
              <button
                onClick={e => onContextMenu(e, conv.id, conv.title, isFav)}
                style={{
                  position: 'absolute', right: 6,
                  width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 5, border: 'none', background: 'transparent',
                  color: '#5A5A5A', cursor: 'pointer', flexShrink: 0,
                }}
                onMouseEnter={e => (e.currentTarget.style.color = '#A0A0A0')}
                onMouseLeave={e => (e.currentTarget.style.color = '#5A5A5A')}
              >
                <MoreHorizontal size={13} />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
